// Studio pricing stores balances as millicredits (1000 = 1 credit). Shared
// conversion + thresholds so Plans UI and builder-chat gates stay aligned with
// the finalized catalog ($0.50 peg).

import { isRecord } from 'src/utils/common_utils';

export const MILLICREDITS_PER_CREDIT = 1000;

// Warn in builder chat when remaining credits fall below this — kept low so
// the nudge only fires when a workspace is genuinely close to the hard
// out-of-credits gate, not just below an arbitrary round number.
export const LOW_BUILD_CREDITS_THRESHOLD = 5;

export const millicreditsToCredits = (millicredits: number): number =>
  millicredits / MILLICREDITS_PER_CREDIT;

/** Credits render with at most one decimal per the Plans-page rounding rule. */
export const formatCredits = (credits: number): string =>
  credits.toLocaleString(undefined, { maximumFractionDigits: 1 });

/**
 * Studio-credit API failures arrive either as the raw axios error thrown by
 * the Amplify API wrapper (body under `response.data`) or as an
 * already-unwrapped RTK error (`data`), so both containers are probed.
 */
export const getStudioCreditErrorBody = (
  error: unknown,
): Record<string, unknown> | undefined => {
  if (!isRecord(error)) {
    return undefined;
  }
  if (isRecord(error.response) && isRecord(error.response.data)) {
    return error.response.data;
  }
  return isRecord(error.data) ? error.data : undefined;
};

/**
 * Whether a balance-query failure is the backend's typed "no balance row"
 * rejection (studio_credit.ErrBalanceNotFound). A workspace with no credit
 * rows at all — legacy Classic, enterprise — is a legitimate non-credit-
 * governed state, not a load failure, so surfaces must not render it as one.
 */
export const isCreditBalanceNotFoundError = (error: unknown): boolean =>
  getStudioCreditErrorBody(error)?.code === 'studio_credit_balance_not_found';
